<!--
  SPDX-License-Identifier: MIT
  Copyright (c) 2026 Solvetia. MIT white-label additions to Chatwoot CE.
-->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  data() {
    return {
      name: '',
      description: '',
      file: null,
    };
  },
  computed: {
    ...mapGetters({
      documents: 'botDocuments/getBotDocuments',
      uiFlags: 'botDocuments/getUIFlags',
    }),
    canSubmit() {
      return this.name.trim() && this.file && !this.uiFlags.isCreating;
    },
  },
  mounted() {
    this.$store.dispatch('botDocuments/get');
  },
  methods: {
    onFileChange(event) {
      this.file = event.target.files[0] || null;
      if (this.file && !this.name.trim()) {
        this.name = this.file.name.replace(/\.[^/.]+$/, '');
      }
    },
    resetForm() {
      this.name = '';
      this.description = '';
      this.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    async save() {
      if (!this.canSubmit) return;
      const formData = new FormData();
      formData.append('name', this.name);
      formData.append('description', this.description);
      formData.append('file', this.file);
      try {
        await this.$store.dispatch('botDocuments/create', formData);
        useAlert(this.$t('BOT_DOCUMENTS.SAVED'));
        this.resetForm();
      } catch (e) {
        useAlert(this.$t('BOT_DOCUMENTS.ERROR'));
      }
    },
    async remove(doc) {
      try {
        await this.$store.dispatch('botDocuments/delete', doc.id);
        useAlert(this.$t('BOT_DOCUMENTS.DELETED'));
      } catch (e) {
        useAlert(this.$t('BOT_DOCUMENTS.ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6 p-6 max-w-2xl">
    <h2 class="text-xl font-medium text-n-slate-12">
      {{ $t('BOT_DOCUMENTS.TITLE') }}
    </h2>
    <p class="text-sm text-n-slate-11">{{ $t('BOT_DOCUMENTS.DESCRIPTION') }}</p>

    <!-- Existing documents -->
    <ul v-if="documents.length" class="flex flex-col gap-2">
      <li
        v-for="doc in documents"
        :key="doc.id"
        class="flex items-center justify-between gap-3 p-3 rounded-lg bg-n-alpha-2"
      >
        <span class="flex flex-col min-w-0">
          <a
            :href="doc.file_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm font-medium text-n-blue-text truncate"
          >{{ doc.name }}</a>
          <span class="text-xs text-n-slate-10 truncate">{{
            doc.description
          }}</span>
        </span>
        <NextButton
          ruby
          ghost
          xs
          :label="$t('BOT_DOCUMENTS.DELETE')"
          @click="remove(doc)"
        />
      </li>
    </ul>
    <p v-else class="text-sm text-n-slate-10">
      {{ $t('BOT_DOCUMENTS.EMPTY') }}
    </p>

    <!-- Upload form -->
    <div class="flex flex-col gap-4 pt-4 border-t border-n-weak">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('BOT_DOCUMENTS.NEW_TITLE') }}
      </h3>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">{{
          $t('BOT_DOCUMENTS.NAME')
        }}</span>
        <input
          v-model="name"
          type="text"
          :placeholder="$t('BOT_DOCUMENTS.NAME_PLACEHOLDER')"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">{{
          $t('BOT_DOCUMENTS.DESCRIPTION_LABEL')
        }}</span>
        <input
          v-model="description"
          type="text"
          :placeholder="$t('BOT_DOCUMENTS.DESCRIPTION_PLACEHOLDER')"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">{{
          $t('BOT_DOCUMENTS.FILE')
        }}</span>
        <input ref="fileInput" type="file" @change="onFileChange" />
      </label>

      <span class="flex items-center gap-2">
        <NextButton
          blue
          :is-loading="uiFlags.isCreating"
          :disabled="!canSubmit"
          :label="$t('BOT_DOCUMENTS.SUBMIT')"
          @click="save"
        />
      </span>
    </div>
  </div>
</template>
